import * as THREE from "three";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFrame } from "@react-three/fiber";
import {
  Text,
  useAnimations,
  useCubeTexture,
  useCursor,
  useGLTF,
} from "@react-three/drei";
import { useRoute } from "wouter";
import { SkeletonUtils } from "three-stdlib";

import { useARSupport } from "../../hooks/useARSupport";
import {
  CANVAS_ANIMATION_RATE,
  scaleAnimationDelta,
} from "../../utils/animationTiming";
import { assetUrl } from "../../utils/paths";
import { publishPortalSceneDebug, summarizeSceneGraph } from "../../utils/sceneDebug";
import { setCurrentMaxxContext } from "../../world-model/runtimeContext";
import { beginMaxxActionVerification } from "../../world-model/maxxActionVerification";
import { launchMaxxAr } from "./ar";
import { useGlobalArTrigger } from "./useGlobalArTrigger";
import { useMaxxVoiceRegistry } from "./useMaxxVoiceRegistry";
import { useMaxxPhaseSync } from "./useMaxxPhaseSync";
import { createTapGestureTracker } from "./tapGesture";
import { publishMaxxPlaybackState } from "./playbackController";
import {
  inferMaxxPhaseFromProgress,
  inferMaxxSemanticStageFromProgress,
} from "../../world-model/maxx";

export const Model1 = ({ position, scale, portalId }) => {
  const group = useRef();
  const { scene, animations } = useGLTF(assetUrl("/n3.glb"), true);
  const { actions } = useAnimations(animations, group);
  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((action) => {
      if (!action) return;
      action.setEffectiveTimeScale?.(CANVAS_ANIMATION_RATE);
      action.paused = !isActive;
      if (isActive && !action.isRunning()) {
        action.reset().play();
      }
    });
  }, [actions, isActive]);

  useLayoutEffect(() => {
    if (!scene) return;
    scene.userData = {
      ...scene.userData,
      entityId: "maxx_receptors",
      objectId: "maxx_receptors",
      portalId: "maxx",
      semanticRole: "receptor and signal-transfer visualization",
      semanticMeaning:
        "A detailed synapse in which receptors and transmitters transfer and carry signals between the Human Neuron and AI Neuron.",
      semanticChildren: [
        {
          id: "maxx_receptor",
          meaning: "Receptors form the receiving side of the detailed synapse, receiving and transferring signals carried between the neurons.",
        },
        {
          id: "maxx_transmitter",
          meaning: "Transmitters carry and send signals across the detailed synaptic space between the neurons.",
        },
      ],
      interactive: false,
    };
  }, [scene]);

  useEffect(() => {
    if (!group.current || !position) return;
    group.current.position.set(...position);
    group.current.scale.set(1.4, 1.4, 1.4);
    group.current.rotation.set(-0.1, 9.4, 0.773);
  }, [position, scale]);

  useEffect(() => {
    if (!scene || portalId !== "maxx") return;

    publishPortalSceneDebug(
      "maxx",
      "Model1",
      summarizeSceneGraph(scene, {
        animationNames: animations?.map((clip) => clip.name) || [],
        state: {
          active: isActive,
          clipStates: Object.entries(actions || {}).map(([name, action]) => ({
            name,
            paused: Boolean(action?.paused),
            running: Boolean(action?.isRunning?.()),
          })),
        },
      })
    );
  }, [actions, animations, isActive, portalId, scene]);

  return (
    <primitive
      ref={group}
      object={scene}
      dispose={null}
      raycast={false}
      receiveShadow={false}
      castShadow={false}
    />
  );
};

export const AnimatedModel = ({ position, scale, portalId }) => {
  const group = useRef();
  const { isMobile } = useARSupport();
  const n2xPath = assetUrl("/n2x.glb");
  const neuroPath = assetUrl("/neurodesign.glb");
  const meetJozEnvFaces = useMemo(
    () =>
      isMobile
        ? ["nx8-m.webp", "px8-m.webp", "py8-m.webp", "ny8-m.webp", "nz8-m.webp", "pz8-m.webp"]
        : ["nx8.webp", "px8.webp", "py8.webp", "ny8.webp", "nz8.webp", "pz8.webp"],
    [isMobile]
  );
  const meetJozEnvMap = useCubeTexture(meetJozEnvFaces, {
    path: assetUrl("/"),
  });
  const { scene: n2xScene, animations: n2xAnims } = useGLTF(n2xPath, true);
  const { actions: n2xActions, mixer: n2xMixer } = useAnimations(n2xAnims, group);

  const glassMat = useMemo(() => {
    const material = new THREE.MeshPhysicalMaterial({
      color: "white",
      metalness: 0,
      roughness: 0,
      transmission: 1,
      ior: 1.5,
      thickness: 0.5,
      reflectivity: 0.9,
      clearcoat: 0.1,
      clearcoatRoughness: 0.3,
      transparent: true,
      depthWrite: false,
    });
    material.iridescenceThicknessRange = [233, 434];
    return material;
  }, []);

  useEffect(() => {
    if (n2xScene && group.current) {
      group.current.updateMatrixWorld(true);
    }
  }, [n2xScene]);

  useLayoutEffect(() => {
    if (!n2xScene) return;
    n2xScene.userData = {
      ...n2xScene.userData,
      entityId: "maxx_neurons",
      objectId: "maxx_neurons",
      portalId: "maxx",
      semanticRole: "primary neuron sequence",
      semanticMeaning:
        "Human and AI intelligence connect, exchange signals, create new experience, and scale new pathways through a biological and neural visual metaphor.",
      semanticChildren: [
        {
          id: "maxx_human_neuron",
          meaning: "Represents the human side of intelligence and lived experience within the neoMAXX signal exchange.",
        },
        {
          id: "maxx_ai_neuron",
          meaning: "Represents machine-side intelligence entering the same signal and pathway metaphor.",
        },
        {
          id: "maxx_synaptic_burst",
          meaning:
            "The central radial burst represents the active detailed synapse as receptors and transmitters transfer and carry signals between the Human Neuron and AI Neuron.",
        },
      ],
      interactive: false,
      action: null,
    };
  }, [n2xScene]);

  const { scene: neuroScene, animations: neuroAnims } = useGLTF(neuroPath);
  const clonedNeuroScene = useMemo(
    () => (neuroScene ? SkeletonUtils.clone(neuroScene) : null),
    [neuroScene]
  );

  useLayoutEffect(() => {
    if (!clonedNeuroScene) return;
    clonedNeuroScene.userData = {
      ...clonedNeuroScene.userData,
      entityId: "maxx_neurodesign",
      objectId: "maxx_neurodesign",
      portalId: "maxx",
      semanticRole: "Gold-Pill explainer and perception-design layer",
      semanticMeaning:
        "The Elite Beauty is the Neurodesign layer inside the Exocortex, combining Ascension, 10/10 Frame Mogg, aura, and perception design.",
      semanticChildren: [
        {
          id: "maxx_ascension",
          meaning: "Ascension is the disciplined process of refinement through consistency, precision, conditioning, and sustained iteration.",
        },
        {
          id: "maxx_frame_mogg",
          meaning: "10/10 Frame Mogg is the structure and perception layer of The Elite Beauty, focused on proportion, symmetry, style, and aura.",
        },
      ],
      interactive: false,
    };
  }, [clonedNeuroScene]);

  useLayoutEffect(() => {
    if (!clonedNeuroScene || !meetJozEnvMap) return;

    const patchMaterial = (material) => {
      if (!material || (material.name || "").trim() !== "1") {
        return material;
      }

      const nextMaterial = material.clone();
      nextMaterial.envMap = meetJozEnvMap;
      nextMaterial.envMapIntensity = Math.max(nextMaterial.envMapIntensity ?? 1, 2.2);
      nextMaterial.needsUpdate = true;
      return nextMaterial;
    };

    clonedNeuroScene.traverse((object) => {
      if (!object.isMesh || !object.material) return;

      if (Array.isArray(object.material)) {
        object.material = object.material.map(patchMaterial);
        return;
      }

      object.material = patchMaterial(object.material);
    });
  }, [clonedNeuroScene, meetJozEnvMap]);

  const neuroMixer = useRef(null);
  useEffect(() => {
    if (!clonedNeuroScene || !neuroAnims?.length) return;
    const mixer = new THREE.AnimationMixer(clonedNeuroScene);
    neuroMixer.current = mixer;
    neuroAnims.forEach((clip) => mixer.clipAction(clip).reset().play());
    return () => {
      mixer.stopAllAction();
      neuroMixer.current = null;
    };
  }, [clonedNeuroScene, neuroAnims]);

  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;
  const [showNeuro, setShowNeuro] = useState(false);
  const maxxCaptureLock = useRef(false);
  const maxxCaptureProgress = useRef(null);
  const hasAppliedN2xInitialPose = useRef(false);

  useEffect(() => {
    if (group.current && position) {
      group.current.position.set(...position);
      group.current.scale.set(0.0135, 0.0135, 0.0135);
    }
  }, [position, scale]);

  const pauseNeurons = useCallback(({ source = "voice" } = {}) => {
    beginMaxxActionVerification("pause_neurons", {
      source,
      deviceClass: isMobile ? "mobile" : "desktop",
    });
    maxxCaptureLock.current = true;
    maxxCaptureProgress.current = null;
    setShowNeuro(true);
    if (neuroMixer.current) neuroMixer.current.timeScale = 1;
  }, [isMobile]);

  const resumeNeurons = useCallback(({ source = "voice" } = {}) => {
    beginMaxxActionVerification("resume_neurons", {
      source,
      deviceClass: isMobile ? "mobile" : "desktop",
    });
    maxxCaptureLock.current = false;
    maxxCaptureProgress.current = null;
    Object.values(n2xActions || {}).forEach((action) => {
      if (!action) return;
      if (!action.isRunning()) action.play();
      action.paused = false;
    });
    setShowNeuro(false);
  }, [isMobile, n2xActions]);

  useMaxxVoiceRegistry({
    actions: n2xActions,
    onPause: pauseNeurons,
    onResume: resumeNeurons,
    logPrefix: "[VoiceModel]",
    registerMessage: "Registered n2x voice handlers (pause/resume flow)",
    unregisterMessage: "Unregistered n2x voice handlers",
  });

  useLayoutEffect(() => {
    if (!isActive || !n2xActions || hasAppliedN2xInitialPose.current) return;

    const clipActions = Object.values(n2xActions).filter(Boolean);
    if (!clipActions.length) return;

    // Apply the authored first frame before the canvas paints. This keeps the
    // three-arrow Spatial Capability group beside its text on initial load.
    clipActions.forEach((action) => {
      action.reset();
      action.clampWhenFinished = false;
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.setEffectiveTimeScale?.(CANVAS_ANIMATION_RATE);
      action.play();
      action.paused = false;
    });
    n2xMixer?.update(0);
    hasAppliedN2xInitialPose.current = true;
  }, [isActive, n2xActions, n2xMixer]);

  useEffect(() => {
    if (!n2xActions) return;
    Object.values(n2xActions).forEach((action) => {
      if (!action) return;
      action.clampWhenFinished = false;
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.setEffectiveTimeScale?.(CANVAS_ANIMATION_RATE);

      const shouldPlay = isActive && !showNeuro && !maxxCaptureLock.current;
      if (shouldPlay && !action.isRunning()) {
        action.play();
      }

      action.paused = !shouldPlay;
    });
  }, [isActive, showNeuro, n2xActions]);

  useMaxxPhaseSync({
    portalId,
    isActive,
    actions: n2xActions,
    paused: showNeuro,
    pausedPhase: "inside_the_brain",
    setCurrentMaxxContext,
  });

  useEffect(() => {
    if (portalId !== "maxx" || !n2xActions) return undefined;
    const readState = () => {
      const clips = Object.entries(n2xActions).flatMap(([name, action]) => {
        const duration = action?.getClip?.()?.duration || 0;
        if (!duration) return [];
        const progress = THREE.MathUtils.clamp((action.time % duration) / duration, 0, 1);
        return [{
          name,
          time: action.time,
          duration,
          progress,
          paused: Boolean(action.paused),
          running: Boolean(action.isRunning?.()),
          semanticStage: inferMaxxSemanticStageFromProgress(progress),
        }];
      });
      const state = {
        observedAt: new Date().toISOString(),
        active: isActive,
        neurodesignVisible: showNeuro,
        clips,
      };
      publishMaxxPlaybackState(state);
      return state;
    };
    const control = (command = "state", value = null) => {
      const clipActions = Object.values(n2xActions).filter((action) => action?.getClip?.());
      if (!clipActions.length) return false;
      if (command === "state") return readState();
      if (command === "seek_progress" || command === "scrub_progress") {
        const progress = THREE.MathUtils.clamp(Number(value), 0, 0.999999);
        const shouldPause = command === "seek_progress" || clipActions.every(
          (action) => action.paused === true
        );
        maxxCaptureLock.current = shouldPause;
        maxxCaptureProgress.current = shouldPause ? progress : null;
        setShowNeuro(shouldPause);
        for (const action of clipActions) {
          const duration = action.getClip()?.duration || 0;
          if (!duration) continue;
          if (!action.isRunning()) action.play();
          action.time = duration * progress;
          action.paused = shouldPause;
        }
        n2xMixer?.update(0);
        setCurrentMaxxContext(
          inferMaxxPhaseFromProgress(progress),
          inferMaxxSemanticStageFromProgress(progress)
        );
        readState();
        return true;
      }
      if (command === "restart") {
        maxxCaptureLock.current = false;
        maxxCaptureProgress.current = null;
        setShowNeuro(false);
        clipActions.forEach((action) => {
          action.reset().play();
          action.paused = false;
        });
        readState();
        return true;
      }
      if (command === "pause") {
        maxxCaptureLock.current = true;
        maxxCaptureProgress.current = null;
        setShowNeuro(true);
        clipActions.forEach((action) => {
          if (!action.isRunning()) action.play();
          action.paused = true;
        });
        n2xMixer?.update(0);
        readState();
        return true;
      }
      if (command === "play" || command === "resume") {
        maxxCaptureLock.current = false;
        maxxCaptureProgress.current = null;
        setShowNeuro(false);
        clipActions.forEach((action) => {
          if (!action.isRunning()) action.play();
          action.paused = false;
        });
        readState();
        return true;
      }
      return false;
    };
    window.__maxxAnimationControl = control;
    readState();
    return () => {
      if (window.__maxxAnimationControl === control) delete window.__maxxAnimationControl;
      publishMaxxPlaybackState(null);
    };
  }, [
    isActive,
    n2xActions,
    n2xMixer,
    portalId,
    setCurrentMaxxContext,
    showNeuro,
  ]);

  useEffect(() => {
    if (!neuroMixer.current) return;
    neuroMixer.current.timeScale = isActive && showNeuro ? 1 : 0;
  }, [isActive, showNeuro]);

  useEffect(() => {
    if (portalId !== "maxx") return;

    publishPortalSceneDebug("maxx", "AnimatedModel", {
      n2x: summarizeSceneGraph(n2xScene, {
        animationNames: n2xAnims?.map((clip) => clip.name) || [],
        interactiveHints: ["qq", "neuron", "space"],
        state: {
          active: isActive,
          showNeuro,
          clipStates: Object.entries(n2xActions || {}).map(([name, action]) => ({
            name,
            paused: Boolean(action?.paused),
            running: Boolean(action?.isRunning?.()),
          })),
        },
      }),
      neurodesign: summarizeSceneGraph(clonedNeuroScene, {
        animationNames: neuroAnims?.map((clip) => clip.name) || [],
        interactiveHints: ["armature", "space", "jkx"],
        state: {
          active: isActive,
          visible: showNeuro,
          mixerActive: Boolean(neuroMixer.current && isActive && showNeuro),
        },
      }),
    });
  }, [
    clonedNeuroScene,
    isActive,
    n2xActions,
    n2xAnims,
    n2xScene,
    neuroAnims,
    portalId,
    showNeuro,
  ]);

  useFrame((_, delta) => {
    const animationDelta = scaleAnimationDelta(delta);
    if (maxxCaptureLock.current && Number.isFinite(maxxCaptureProgress.current)) {
      const progress = maxxCaptureProgress.current;
      Object.values(n2xActions || {}).forEach((action) => {
        const duration = action?.getClip?.()?.duration || 0;
        if (!duration) return;
        action.time = duration * progress;
        action.paused = true;
      });
      n2xMixer?.update(0);
    }

    if (n2xMixer && isActive && !showNeuro && !maxxCaptureLock.current) {
      n2xMixer.update(animationDelta * 0.4);
    }

    if (neuroMixer.current && isActive && showNeuro) {
      neuroMixer.current.update(animationDelta * 0.4);
    }
  });

  const alphaNeuro = useRef(0);
  useFrame((_, delta) => {
    const animationDelta = scaleAnimationDelta(delta);
    const target = showNeuro ? 1 : 0;
    alphaNeuro.current = THREE.MathUtils.damp(
      alphaNeuro.current,
      target,
      3,
      animationDelta
    );
    const opacity = alphaNeuro.current;
    const isFading = opacity > 0.001 && opacity < 0.999;
    const visible = opacity > 0.02;

    clonedNeuroScene?.traverse((object) => {
      if (!object.isMesh || !object.material) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];

      materials.forEach((material) => {
        if (!material) return;

        if (material.userData.__baseTransparent === undefined) {
          material.userData.__baseTransparent = material.transparent;
          material.userData.__baseDepthWrite = material.depthWrite;
          material.userData.__baseOpacity = material.opacity ?? 1;
        }

        material.transparent = isFading || opacity < 0.999 || material.userData.__baseTransparent;
        material.depthWrite = !isFading && visible && material.userData.__baseDepthWrite !== false;
        material.opacity = (material.userData.__baseOpacity ?? 1) * opacity;
      });

      object.visible = visible;
      object.raycast = visible ? THREE.Mesh.prototype.raycast : () => {};
    });
  });

  useEffect(() => {
    if (!n2xScene) return;

    const timer = setTimeout(() => {
      const mesh = n2xScene.getObjectByName("qq.002");
      if (!mesh) {
        console.warn("⚠️ mesh not found, check console dump above");
        return;
      }

      console.log("🎯 found", mesh.name, "with material", mesh.material?.name);

      if (mesh.material?.name === "glassX") {
        mesh.material = glassMat.clone();
        mesh.material.needsUpdate = true;
        console.log("✅ glassMat applied directly");
      } else {
        console.warn("⚠️ material name was", mesh.material?.name);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [glassMat, n2xScene]);

  return (
    <>
      {n2xScene && (
        <primitive
          ref={group}
          object={n2xScene}
          dispose={null}
        />
      )}

      {clonedNeuroScene && (
        <group position={[0, 0.1, -2.42]} scale={[0.01, 0.01, 0.01]}>
          <primitive
            object={clonedNeuroScene}
          />
        </group>
      )}
    </>
  );
};

export function AnimatedModelMobile({
  position = [0, -0.78, -2],
  scale = [0.0135, 0.0135, 0.0135],
  portalId,
  playbackSpeed = 0.5,
  arUsdzUrl = "https://meetjoz.com/neurodesign.usdz",
  arGlbUrl = "https://meetjoz.com/neurovibes.glb",
}) {
  const group = useRef();
  const { scene: meSrc, animations } = useGLTF(assetUrl("/n2xm.glb"));
  const me = useMemo(() => (meSrc ? SkeletonUtils.clone(meSrc) : null), [meSrc]);
  const { actions, mixer } = useAnimations(animations, me);
  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;
  const [isPaused, setIsPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { isMobile } = useARSupport();
  useCursor(hovered, "pointer");
  const pauseNeurons = useCallback(({ source = "voice" } = {}) => {
    beginMaxxActionVerification("pause_neurons", {
      source,
      deviceClass: "mobile",
    });
    setIsPaused(true);
  }, []);
  const resumeNeurons = useCallback(({ source = "voice" } = {}) => {
    beginMaxxActionVerification("resume_neurons", {
      source,
      deviceClass: "mobile",
    });
    setIsPaused(false);
  }, []);

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((action) => {
      if (!action) return;
      action.clampWhenFinished = false;
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.setEffectiveTimeScale?.(playbackSpeed);

      if (isActive && !action.isRunning()) {
        action.play();
      }

      action.paused = !isActive || isPaused;
    });
  }, [actions, isActive, isPaused, playbackSpeed]);

  const launchAR = useCallback(() => {
    launchMaxxAr({ arUsdzUrl, arGlbUrl });
  }, [arGlbUrl, arUsdzUrl]);
  const tapGesture = useMemo(() => createTapGestureTracker(), []);

  useGlobalArTrigger(launchAR, "AnimatedModelMobile");
  useMaxxVoiceRegistry({
    enabled: isMobile,
    actions,
    onPause: pauseNeurons,
    onResume: resumeNeurons,
    logPrefix: "[VoiceModelMobile]",
    registerMessage: "Registered n2x voice handler for mobile",
    unregisterMessage: "Unregistered n2x voice handler for mobile",
  });

  useMaxxPhaseSync({
    portalId,
    isActive,
    actions,
    paused: isPaused,
    pausedPhase: "inside_the_brain",
    activeFallbackPhase: "signal_flow",
    setCurrentMaxxContext,
  });

  useEffect(() => {
    if (portalId !== "maxx" || !actions) return undefined;

    const readState = () => {
      const clips = Object.entries(actions).flatMap(([name, action]) => {
        const duration = action?.getClip?.()?.duration || 0;
        if (!duration) return [];
        const progress = THREE.MathUtils.clamp((action.time % duration) / duration, 0, 1);
        return [{
          name,
          time: action.time,
          duration,
          progress,
          paused: Boolean(action.paused),
          running: Boolean(action.isRunning?.()),
          semanticStage: inferMaxxSemanticStageFromProgress(progress),
        }];
      });
      const state = {
        observedAt: new Date().toISOString(),
        active: isActive,
        neurodesignVisible: false,
        clips,
      };
      publishMaxxPlaybackState(state);
      return state;
    };

    const control = (command = "state", value = null) => {
      const clipActions = Object.values(actions).filter((action) => action?.getClip?.());
      if (!clipActions.length) return false;
      if (command === "state") return readState();
      if (command === "seek_progress" || command === "scrub_progress") {
        const progress = THREE.MathUtils.clamp(Number(value), 0, 0.999999);
        const shouldPause = command === "seek_progress" || clipActions.every(
          (action) => action.paused === true
        );
        setIsPaused(shouldPause);
        clipActions.forEach((action) => {
          const duration = action.getClip()?.duration || 0;
          if (!duration) return;
          if (!action.isRunning()) action.play();
          action.time = duration * progress;
          action.paused = shouldPause;
        });
        mixer?.update(0);
        setCurrentMaxxContext(
          inferMaxxPhaseFromProgress(progress),
          inferMaxxSemanticStageFromProgress(progress)
        );
        readState();
        return true;
      }
      if (command === "restart") {
        setIsPaused(false);
        clipActions.forEach((action) => {
          action.reset().play();
          action.paused = false;
        });
        readState();
        return true;
      }
      if (command === "pause") {
        setIsPaused(true);
        clipActions.forEach((action) => {
          if (!action.isRunning()) action.play();
          action.paused = true;
        });
        mixer?.update(0);
        readState();
        return true;
      }
      if (command === "play" || command === "resume") {
        setIsPaused(false);
        clipActions.forEach((action) => {
          if (!action.isRunning()) action.play();
          action.paused = false;
        });
        readState();
        return true;
      }
      return false;
    };

    window.__maxxAnimationControl = control;
    readState();
    return () => {
      if (window.__maxxAnimationControl === control) delete window.__maxxAnimationControl;
      publishMaxxPlaybackState(null);
    };
  }, [actions, isActive, mixer, portalId]);

  if (!me) return null;

  return (
    <group
      ref={group}
      position={position}
      scale={scale}
      onPointerDown={(event) => {
        event.stopPropagation();
        tapGesture.start(event);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        tapGesture.move(event);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        if (tapGesture.end(event)) launchAR();
      }}
      onPointerCancel={() => tapGesture.cancel()}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <primitive object={me} />
      <Text position={[0, 1.2, 0]} fontSize={0.1} color="white">
        Open in Space
      </Text>
    </group>
  );
}

export function AnimatedModelMobilexr({
  position = [0, -0.78, -2],
  scale = [0.0135, 0.0135, 0.0135],
  portalId,
  playbackSpeed = 0.5,
  arUsdzUrl = "https://meetjoz.com/neurodesign.usdz",
  arGlbUrl = "https://meetjoz.com/neurovibes.glb",
}) {
  const group = useRef();
  const { scene: meSrc, animations } = useGLTF(assetUrl("/n2xmar.glb"));
  const me = useMemo(() => (meSrc ? SkeletonUtils.clone(meSrc) : null), [meSrc]);
  const { actions } = useAnimations(animations, me);
  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;
  const [hovered, setHovered] = useState(false);
  useCursor(hovered, "pointer");

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((action) => {
      if (!action) return;
      action.clampWhenFinished = false;
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.setEffectiveTimeScale?.(playbackSpeed);

      if (isActive && !action.isRunning()) {
        action.play();
      }

      action.paused = !isActive;
    });
  }, [actions, isActive, playbackSpeed]);

  const launchAR = useCallback(() => {
    launchMaxxAr({ arUsdzUrl, arGlbUrl });
  }, [arGlbUrl, arUsdzUrl]);
  const tapGesture = useMemo(() => createTapGestureTracker(), []);

  useGlobalArTrigger(launchAR, "AnimatedModelMobilexr");

  if (!me) return null;

  const { isMobile } = useARSupport();
  useMaxxVoiceRegistry({
    enabled: isMobile,
    actions,
    onPause: () => {
      console.log("🧠 [Mobile Voice] Pausing n2x.glb animation");
    },
    onResume: () => {
      console.log("🧠 [Mobile Voice] Resuming n2x.glb animation");
    },
    logPrefix: "[VoiceModelMobile]",
    registerMessage: "Registered n2x voice handler for mobile",
    unregisterMessage: "Unregistered n2x voice handler for mobile",
  });

  useMaxxPhaseSync({
    portalId,
    isActive,
    actions,
    activeFallbackPhase: "signal_flow",
    setCurrentMaxxContext,
  });

  return (
    <group
      ref={group}
      position={position}
      scale={scale}
      onPointerDown={(event) => {
        event.stopPropagation();
        tapGesture.start(event);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        tapGesture.move(event);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        if (tapGesture.end(event)) launchAR();
      }}
      onPointerCancel={() => tapGesture.cancel()}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <primitive object={me} />
      <Text position={[0, 1.2, 0]} fontSize={0.1} color="white">
        Open in Space
      </Text>
    </group>
  );
}

export const Aura = ({
  position = [0, -0.6, 18],
  scale = [3, 3, 3],
  portalId,
  playbackSpeed = 0.5,
}) => {
  const group = useRef();
  const { scene, animations } = useGLTF(assetUrl("/aura.glb"));
  const { actions, mixer } = useAnimations(animations, group);
  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;

  useEffect(() => {
    if (!group.current) return;
    group.current.position.set(...position);
    group.current.scale.set(...scale);
  }, [position, scale]);

  useLayoutEffect(() => {
    if (!scene) return;
    scene.userData = {
      ...scene.userData,
      entityId: "root_aura",
      objectId: "root_aura",
      portalId: "root",
      semanticRole: "three-ring aura field",
      semanticMeaning:
        "The three animated rings represent Aura: to Ascend and shine through glow, presence, radiance, and the perceptual field of the app UX.",
      interactive: false,
    };
  }, [scene]);

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((action) => {
      if (!action) return;
      action.reset()?.play();
      action.paused = !isActive;
      action.setEffectiveTimeScale(playbackSpeed);
    });

    if (mixer) mixer.timeScale = playbackSpeed;
    return () => mixer?.stopAllAction();
  }, [actions, isActive, mixer, playbackSpeed]);

  useFrame((_, delta) => {
    if (mixer && isActive) {
      mixer.update(scaleAnimationDelta(delta) * playbackSpeed);
    }
  });

  return (
    <primitive
      ref={group}
      object={scene}
      dispose={null}
      raycast={false}
      receiveShadow={false}
      castShadow={false}
    />
  );
};
